import React, { useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { formatTime12Hour } from '../../../utils/teamWorkingHoursHelper';

interface OutsideWorkingHoursConfirmationModalProps {
    open: boolean;
    warning: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const OutsideWorkingHoursConfirmationModal: React.FC<OutsideWorkingHoursConfirmationModalProps> = ({ open, warning, onConfirm, onCancel }) => {
    const formattedWarning = useMemo(() => {
        if (!warning) return warning;

        // Handle multiple teams (split by newlines)
        const lines = warning.split('\n');
        const formattedLines = lines.map((line) => {
            const timeRangePattern = /(\d{2}:\d{2})-(\d{2}:\d{2})/;
            const match = line.match(timeRangePattern);

            if (match) {
                const startTime24 = match[1];
                const endTime24 = match[2];
                const startTime12 = formatTime12Hour(startTime24);
                const endTime12 = formatTime12Hour(endTime24);
                return line.replace(timeRangePattern, `between ${startTime12} and ${endTime12}`);
            }

            return line;
        });

        return formattedLines.join('\n');
    }, [warning]);

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="h6">Appointment Outside Team Hours</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                    {formattedWarning}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Would you like to schedule this appointment anyway?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="inherit">
                    Cancel
                </Button>
                <Button onClick={onConfirm} variant="contained" color="primary">
                    Schedule Anyway
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OutsideWorkingHoursConfirmationModal;
